import moment from 'moment';
import { normalizeString } from '../../../../../../util/stringUtils';

/**
 * Valida las filas parseadas del Excel contra los datos existentes cargados en la aplicación.
 *
 * Fuentes de datos del contexto:
 *   - sectores:               state.resolucion.sectores.data        (Pantalla 2)
 *   - puestos:                state.resolucion.puestos.data         (Pantalla 2)
 *   - sustanciasFormulario:   state.resolucion.postResolucion.sustancias  (Pantalla 3 - cargadas en el formulario)
 *   - sustanciasDisponibles:  state.resolucion.sustancias.data      (Todas las sustancias del sistema)
 *   - puestosAsociados:       state.resolucion.postResolucion.puestos (Pantalla 5)
 *   - nominasActivas:         state.resolucion.nominasActivasEstablecimiento.data
 *                             Array de { id, nombre } donde nombre = "Apellido, Nombre (CUIL) - Ingreso: DD/MM/YYYY"
 *
 * @param {Array} rows - Filas parseadas desde parseExcelFile
 * @param {Object} context - { sectores, puestos, sustanciasFormulario, sustanciasDisponibles, puestosAsociados, nominasActivas }
 * @returns {{ valid: boolean, errors: Array, workers: Array }}
 */
export const validateExcelData = (rows, { sectores, puestos, sustanciasFormulario, sustanciasDisponibles, puestosAsociados, nominasActivas, establecimientoMU }) => {
    if (!rows || rows.length === 0) {
        return { valid: false, errors: [{ cuil: 'GENERAL', messages: ['El archivo no contiene datos.'] }], workers: [] };
    }

    // --- Verificar CUILs identificables (Error de formato genérico) ---
    const rowsWithCuil = rows.filter(r => r.cuil && r.cuil.toString().trim().length === 11);
    if (rowsWithCuil.length === 0) {
        return { valid: false, errors: [{ cuil: 'GENERAL', messages: ['El formato del archivo no es el correcto. No se pudieron identificar CUILs válidos.'] }], workers: [] };
    }

    const maxInicioExposicion = moment().subtract(1, 'year').endOf('year');
    const maxFechaIngreso = moment().subtract(1, 'year').endOf('year');

    // ==========================================
    // 1. Construir mapas de búsqueda
    // ==========================================
    const sectorMap = new Map();
    (sectores || []).forEach((s) => sectorMap.set(normalizeString(s.nombre), s));

    const puestoMap = new Map();
    (puestos || []).forEach((p) => puestoMap.set(normalizeString(p.nombre), p));

    // Map de sustancias del formulario (para validar asociaciones con puestos)
    const sustanciaFormularioByCode = new Map();
    (sustanciasFormulario || []).forEach((s) => {
        const code = s.sustancia?.nombreAgente?.toString()?.trim();
        if (code) sustanciaFormularioByCode.set(code, s);
    });

    // Map de TODAS las sustancias disponibles del sistema (para validar si el código es válido)
    const sustanciaDisponibleByCode = new Map();
    (sustanciasDisponibles || []).forEach((s) => {
        const nombreCompleto = s.nombre?.toString()?.trim();
        if (nombreCompleto) {
            // Extraer solo el código (parte antes del " - ")
            const code = nombreCompleto.split(" - ")[0]?.trim();
            if (code) sustanciaDisponibleByCode.set(code, s);
        }
    });

    const puestoSustanciasMap = new Map();
    (puestosAsociados || []).forEach((p) => puestoSustanciasMap.set(p.puesto, p.sustancias || []));

    const nominasByCuil = new Map();
    (nominasActivas || []).forEach((n) => {
        // Formato estructurado del endpoint nomina/activasEstablecimiento
        if (n.cuil) {
            nominasByCuil.set(n.cuil, {
                ...n,
                fechaIngreso: n.fechaIngreso ? moment(n.fechaIngreso).format("DD/MM/YYYY") : null
            });
            return;
        }

        // Formato legacy (concatenado)
        const match = n.nombre?.match(/\((\d{11})\)/);
        const fechaMatch = n.nombre?.match(/Ingreso: ([\d/]+)/);
        if (match) {
            nominasByCuil.set(match[1], {
                ...n,
                fechaIngreso: fechaMatch ? fechaMatch[1] : null
            });
        }
    });

    // ==========================================
    // 2. Agrupar filas por CUIL
    // ==========================================
    const rowsByCuil = new Map();
    rows.forEach(row => {
        const cuil = row.cuil ? row.cuil.toString().trim() : 'INVALID';
        if (!rowsByCuil.has(cuil)) rowsByCuil.set(cuil, []);
        rowsByCuil.get(cuil).push(row);
    });

    const validatedWorkers = [];
    const groupedErrors = new Map();

    const addError = (cuil, rowNum, message) => {
        const key = cuil || `ROW_${rowNum}`;
        if (!groupedErrors.has(key)) {
            groupedErrors.set(key, { cuil: cuil || 'Sin CUIL', row: rowNum, messages: [] });
        }
        if (!groupedErrors.get(key).messages.includes(message)) {
            groupedErrors.get(key).messages.push(message);
        }
    };

    // ==========================================
    // 3. Procesar cada trabajador (grupo por CUIL)
    // ==========================================
    rowsByCuil.forEach((workerRows, cuil) => {
        const firstRowRaw = workerRows[0];
        const rowNum = firstRowRaw.rowNumber;
        const workerErrors = [];

        // Función auxiliar para normalizar cualquier fecha de entrada a string YYYY-MM-DD de forma segura
        const normalizeDateStr = (dateInput) => {
            if (!dateInput) return null;
            const m = moment(dateInput);
            if (!m.isValid()) return null;
            return m.format('YYYY-MM-DD');
        };

        const firstRow = {
            ...firstRowRaw,
            fechaIngreso: normalizeDateStr(firstRowRaw.fechaIngreso)
        };

        // 3.1 Verificación básica de CUIL
        if (!cuil || cuil === 'INVALID') {
            addError(cuil, rowNum, 'El CUIL es obligatorio o tiene un formato inválido.');
            return;
        }

        // 3.2 Verificar contra nóminas activas
        const activeNomina = nominasByCuil.get(cuil);
        if (!activeNomina) {
            workerErrors.push('El CUIL no se encuentra dentro de las nominas activas.');
        }

        // 3.3 Verificación de MU
        if (establecimientoMU && firstRow.mu && firstRow.mu.toString().trim() !== establecimientoMU.toString().trim()) {
            workerErrors.push(`La MU del archivo ("${firstRow.mu}") no coincide con la del establecimiento actual ("${establecimientoMU}").`);
        }

        // 3.4 Consistencia entre filas para el mismo trabajador
        const sectors = new Set(workerRows.filter(r => r.sector).map(r => normalizeString(r.sector)));
        const puestosNames = new Set(workerRows.filter(r => r.puesto).map(r => normalizeString(r.puesto)));
        if (sectors.size > 1) workerErrors.push('El trabajador tiene diferentes sectores en distintas filas.');
        if (puestosNames.size > 1) workerErrors.push('El trabajador tiene diferentes puestos en distintas filas.');

        // 3.5 Existencia de Sector y Puesto
        const sectorObj = sectorMap.get(normalizeString(firstRow.sector || ''));
        const puestoObj = puestoMap.get(normalizeString(firstRow.puesto || ''));

        if (!firstRow.sector) {
            workerErrors.push('El campo Sector es obligatorio.');
        } else if (!sectorObj) {
            workerErrors.push(`El Sector "${firstRow.sector}", no se encuentra registrado Verificar y modificar o registrar de ser necesario.`);
        }

        if (!firstRow.puesto) {
            workerErrors.push('El campo Puesto es obligatorio.');
        } else if (!puestoObj) {
            workerErrors.push(`El Puesto "${firstRow.puesto}", no se encuentra registrado Verificar y modificar o registrar de ser necesario.`);
        }

        // 3.5.1 Fecha Ingreso obligatoria
        if (!firstRow.fechaIngreso) {
            workerErrors.push('La fecha de ingreso a la empresa debe ser obligatoria.');
        } else if (moment(firstRow.fechaIngreso).isAfter(maxFechaIngreso, 'day')) {
            workerErrors.push('La fecha de ingreso no puede ser posterior al 31/12 del año anterior.');
        }

        // 3.6 Coincidencia de Fecha de Ingreso
        if (activeNomina && firstRow.fechaIngreso) {
            const excelDateFormatted = moment(firstRow.fechaIngreso).format("DD/MM/YYYY");
            const activeDateFormatted = activeNomina.fechaIngreso;

            if (activeDateFormatted && excelDateFormatted !== activeDateFormatted) {
                workerErrors.push(`La Fecha de Ingreso ("${excelDateFormatted}") no coincide con la de la nómina activa ("${activeDateFormatted}").`);
            }
        }

        // 3.7 Procesar sustancias y fechas
        const examenes = {};
        const declaredSubstancesInExcel = new Set();
        let someSubstanceErrors = false;

        workerRows.forEach(row => {
            const hasSustancia = !!row.sustancia;
            const code = row.sustancia ? row.sustancia.toString().trim() : '';

            // Normalizar fechas de la fila inmediatamente
            const rowInicio = normalizeDateStr(row.fechaInicioExposicion);
            const rowFin = normalizeDateStr(row.fechaFinExposicion);

            const requiereDatosExamenes = hasSustancia || !!rowInicio;
            if (requiereDatosExamenes) {
                const requiredExamColumns = [
                    { key: 'permanente', label: 'Permanente' },
                    { key: 'preocupacional', label: 'Preocupacional' },
                    { key: 'periodico', label: 'Periódico' },
                    { key: 'previoTarea', label: 'Previo a cada tarea' },
                    { key: 'ausenciaProlongada', label: 'Ausencia Prolongada' },
                    { key: 'egreso', label: 'Egreso' },
                ];
                const informacionIncompleta = requiredExamColumns.some(({ key }) => row[key] !== 0 && row[key] !== 1);
                if (informacionIncompleta) {
                    workerErrors.push('Información de exámenes incompleta');
                }
            }

            if (rowInicio && moment(rowInicio).isAfter(maxInicioExposicion, 'day')) {
                workerErrors.push('La fecha de inicio de exposición no puede ser posterior al 31/12 del año anterior.');
            }

            if (rowInicio && firstRow.fechaIngreso && moment(rowInicio).isBefore(moment(firstRow.fechaIngreso), 'day')) {
                workerErrors.push('La fecha de inicio de exposición no puede ser anterior a la fecha de ingreso a la empresa.');
            }

            if (hasSustancia) {
                declaredSubstancesInExcel.add(code);

                // Primero verificar si el código corresponde a un agente cancerígeno válido
                const sustanciaDisponible = sustanciaDisponibleByCode.get(code);
                if (!sustanciaDisponible) {
                    workerErrors.push(`La sustancia "${code}" no corresponde a un Agente Cancerígeno`);
                    someSubstanceErrors = true;
                } else {
                    // Luego verificar si está cargada en el formulario y asociada al puesto
                    const sustanciaFormulario = sustanciaFormularioByCode.get(code);
                    if (!sustanciaFormulario) {
                        workerErrors.push(`La sustancia "${code}" no está registrada en el formulario (Pantalla 3).`);
                        someSubstanceErrors = true;
                    } else if (puestoObj) {
                        const asociadas = puestoSustanciasMap.get(puestoObj.id);
                        if (!asociadas || !asociadas.includes(sustanciaFormulario.id)) {
                            workerErrors.push(`La sustancia "${code}" no está asociada al puesto "${row.puesto}" en el formulario.`);
                            someSubstanceErrors = true;
                        }
                    }
                }

                // Validación de fechas para sustancia usando valores normalizados
                if (rowInicio && rowFin) {
                    if (moment(rowInicio).isAfter(moment(rowFin), 'day')) {
                        workerErrors.push('la Fecha de Fin de exposición No puede ser Anterior a la Fecha de Inicio de exposición.');
                    }
                }

                if (rowFin) {
                    if (!rowInicio) {
                        workerErrors.push('No puede informar fecha de fin a un caso sin fecha de inicio');
                    }
                    if (firstRow.fechaIngreso && moment(rowFin).isBefore(moment(firstRow.fechaIngreso), 'day')) {
                        workerErrors.push('la Fecha de Fin de exposición No puede ser Anterior a la Fecha de Ingreso a la Empresa.');
                    }
                }

                // Recopilar exámenes y fechas
                const sustanciaFormulario = sustanciaFormularioByCode.get(code);
                if (sustanciaFormulario && !someSubstanceErrors) {
                    examenes[sustanciaFormulario.id] = {
                        inicioContacto: rowInicio,
                        finContacto: rowFin,
                        [1]: { value: row.preocupacional === 1 },
                        [2]: { value: row.periodico === 1 },
                        [3]: { value: row.previoTarea === 1 },
                        [4]: { value: row.ausenciaProlongada === 1 },
                        [5]: { value: row.egreso === 1 },
                    };
                }
            } else {
                // ¿Sin sustancia pero la fila tiene fechas?
                if (rowInicio) {
                    workerErrors.push('la fecha de inicio es solo para casos con exposición a la sustancia, Verificar');
                }
            }
        });

        // 3.8 Consistencia Puesto vs Sustancias (Conteos y asociación)
        // Solo validar conteos si no hay errores específicos de asociación
        if (puestoObj && !someSubstanceErrors) {
            const expectedSubstancesIds = puestoSustanciasMap.get(puestoObj.id) || [];
            const expectedCodes = new Set();
            expectedSubstancesIds.forEach(id => {
                const s = sustanciasFormulario.find(sust => sust.id === id);
                const c = s?.sustancia?.nombreAgente?.toString()?.trim();
                if (c) expectedCodes.add(c);
            });

            if (expectedCodes.size > 0 && declaredSubstancesInExcel.size === 0) {
                workerErrors.push(`verifique el CUIL “${cuil}” quien posee menos sustancias que las declaradas para el puesto`);
            } else if (declaredSubstancesInExcel.size > expectedCodes.size) {
                workerErrors.push(`verifique el CUIL “${cuil}” quien posee más sustancias que las declaradas para el puesto`);
            } else if (declaredSubstancesInExcel.size < expectedCodes.size && declaredSubstancesInExcel.size > 0) {
                workerErrors.push(`verifique el CUIL “${cuil}” quien posee menos sustancias que las declaradas para el puesto`);
            }
        }

        if (workerErrors.length > 0) {
            workerErrors.forEach(msg => addError(cuil, rowNum, msg));
        } else {
            // Fechas de nivel raíz para visualización en tabla/compatibilidad legacy (desde primera sustancia)
            const subIds = Object.keys(examenes);
            const firstSubData = subIds.length > 0 ? examenes[subIds[0]] : {};

            validatedWorkers.push({
                cuil: firstRow.cuil,
                idTrabajador: activeNomina?.idNomina || activeNomina?.id || null,
                nombreApellidoTrabajador: firstRow.apellidoNombre,
                fechaIngreso: firstRow.fechaIngreso,
                nombreSector: firstRow.sector,
                nombrePuesto: firstRow.puesto,
                idSector: sectorObj?.id ?? null,
                idPuesto: puestoObj?.id ?? null,
                permanente: firstRow.permanente === 1,
                exposicion: Object.keys(examenes).length === 0,
                examenes: examenes,
                // Campos para visualización en tabla
                sustanciaCodigo: Array.from(declaredSubstancesInExcel).join(', '),
                inicioContacto: firstSubData.inicioContacto || null,
                finContacto: firstSubData.finContacto || null,
                importadoDesdeExcel: true,
            });
        }
    });

    const errorsList = Array.from(groupedErrors.values());

    return {
        valid: errorsList.length === 0,
        errors: errorsList,
        workers: errorsList.length === 0 ? validatedWorkers : [],
    };
};
